import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import anime from "animejs";
import { PixiSettings, FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../pixiSettings";
import { Settings } from "../../../../settings";
import { getAssetsPath } from "../../../../utils/pathConfig";
import { ASPECT_RATIO_BREAKPOINTS } from "../../../../Contexts/DimensionsContext";
import { createButtonPressAnimation } from "../../../../utilities";

// Black background for loader
const LOADER_BACKGROUND_COLOR = 0x000000;

// ── Identical to WarningPopup / InfoPopup ────────────────────────────────────
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

const POPUP_SCALE_DURATION = 400;
const POPUP_SCALE_EASING = "easeOutBack";
const POPUP_SCALE_DELAY = 150;

const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.8;

const BUTTON_REF_WIDTH = 160;
const BUTTON_REF_HEIGHT = 64;

const PANEL_REF_WIDTH = 504;
const PANEL_REF_HEIGHT = 342;
const DESKTOP_SCALE_REF = 1.175;

// Same multipliers / base values as WarningPopup ERROR_POPUP_CONFIG
const ERROR_POPUP_CONFIG = {
  REFERENCE_WIDTH: 600,
  basePaddingH: 22,
  basePaddingV: 42,
  baseMessageFontSize: 20,
  baseButtonFontSize: 24,
  baseTextButtonGap: 55,
  baseBorderRadius: 20,
  baseMinEdgePadding: 20,
  baseContentWidth: 370,
  DEVICE_CONFIGS: {
    MOBILE_SMALL: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.575 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.65 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.79 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7125 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.7 },
    },
    SMALL_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.725 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.97 },
    },
    WIDE_DESKTOP: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.14 },
    },
  },
};

// Layout multipliers identical to WarningPopup
const TEXT_AREA_TOP_MULTIPLIER = 1.0;
const BOTTOM_PADDING_MULTIPLIER = 0.4;
const TEXT_TO_BUTTON_GAP_MULTIPLIER = 0.3;
const BUTTON_EXTRA_LIFT_MULTIPLIER = 0.36;

// ============================================================================
// LOADER SPINE CONFIGURATION
// Same pattern as WinAnimationSecondaryController DEVICE_MULTIPLIERS.
// scale:  multiplier on top of the base symbolHeight/SYMBOL_HEIGHT ratio
// offset: spine Y offset multiplier (fraction of symbolHeight, 0 = center)
// ============================================================================

const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  scale: 1.2,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  scale: 1.4,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  scale: 1.5,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  scale: 1.3,
  offset: 0.0,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  scale: 1.0,
  offset: 0.0,
};

/**
 * LoaderController — Handles the secondary preloader screen.
 *
 * Uses a single "preloader" spine that contains both the logo and loader
 * animation. The spine is centered on screen with device-specific scale
 * and Y offset (same pattern as the primary LoaderController).
 *
 * Flow: load spine → play "popiplay_logo" (looping) → onLoadingComplete()
 *       → wait for current loop to finish → resolve → Loader.jsx fades out.
 */
class LoaderController {
  constructor(container, dimensions, symbolHeight, dimensionsContext) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    this.dimensionsContext = dimensionsContext;

    // Visual elements
    this.preloaderSpine = null;

    // State
    this.isPlaying = false;
    this.initialized = false;
    this.errorOccurred = false;

    // Loading lifecycle — dual-flag pattern (mirrors primary loader)
    // Ensures at least one full spine loop plays before transitioning
    this.firstLoopComplete = false;
    this.loadingComplete = false;
    this._outResolve = null;

    // Error UI (WarningPopup-identical layout)
    this.errorRoot = null;
    this.errorContainer = null; // content container (scaled)
    this._errorOverlay = null;
    this._errorMessage = null;
    this._errorButtonSprite = null;
    this._isErrorPressAnimating = false;
    this._errorBrightnessFilter = null;

    // Clickable fullscreen background (mirrors IntroPopupComposedController)
    this._backgroundGraphics = null;
    this._backgroundClickHandler = null;

    // Caches
    this._availableAnimationsCache = null;
    this._deviceMultipliersCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    // Full-screen clickable background — tap anywhere to enter fullscreen
    this._createClickableBackground();

    // Load spine and start animation
    this.initialize();
  }

  // =========================================================================
  // Clickable background — fullscreen on tap (IntroPopup pattern)
  // =========================================================================

  _createClickableBackground() {
    this._backgroundGraphics = new PIXI.Graphics();
    this._backgroundGraphics.zIndex = 0;
    this._backgroundGraphics.eventMode = "static";
    this._backgroundGraphics.cursor = "default";
    this._backgroundGraphics.on("pointertap", (e) => {
      if (this._backgroundClickHandler) this._backgroundClickHandler(e);
    });
    this.container.addChild(this._backgroundGraphics);
    this.container.alpha = 1;
    this._updateBackgroundDimensions();
  }

  _updateBackgroundDimensions() {
    if (!this._backgroundGraphics || !this.dimensions) return;

    const { width, height } = this.dimensions;
    this._backgroundGraphics.clear();
    this._backgroundGraphics.beginFill(LOADER_BACKGROUND_COLOR);
    this._backgroundGraphics.drawRect(0, 0, width, height);
    this._backgroundGraphics.endFill();
  }

  setBackgroundClickHandler(handler) {
    this._backgroundClickHandler = handler;
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  async initialize() {
    try {
      const BASE_PATH = getAssetsPath();
      const jsonPath = `${BASE_PATH}/spines/preloader/preloader.json`;
      const atlasPath = `${BASE_PATH}/spines/preloader/preloader.atlas`;
      const skeletonAlias = "preloader-skeleton";
      const atlasAlias = "preloader-atlas";

      if (!PIXI.Assets.cache.has(skeletonAlias)) {
        PIXI.Assets.add({ alias: skeletonAlias, src: jsonPath });
        PIXI.Assets.add({ alias: atlasAlias, src: atlasPath });
        await PIXI.Assets.load([skeletonAlias, atlasAlias]);
      }

      // If error occurred while loading, don't create spine
      if (this.errorOccurred) return;

      this.preloaderSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!this.preloaderSpine?.skeleton?.data?.animations) return;

      this._availableAnimationsCache = null;

      this.container.addChild(this.preloaderSpine);
      this.preloaderSpine.alpha = 1;
      this.preloaderSpine.zIndex = 10;
      this.preloaderSpine.eventMode = "none";
      this.initialized = true;

      this.applyScaleAndCenter();
      this.playAnimation();
    } catch (error) {
      // Silent fail — preloader will continue without animation
    }
  }

  // =========================================================================
  // Positioning — same pattern as WinAnimationSecondaryController
  // =========================================================================

  /**
   * Get device/orientation-specific multipliers.
   * Cached until device or orientation changes.
   */
  getDeviceMultipliers() {
    const { device, orientation } = this.dimensionsContext || {};

    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.MOBILE_SMALL) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    return multipliers;
  }

  /**
   * Apply proportional scaling and centering to spine.
   * Same pattern as WinAnimationSecondaryController.applyScaleAndCenter:
   * base scale = symbolHeight / SYMBOL_HEIGHT, then × device multiplier.
   */
  applyScaleAndCenter() {
    if (!this.preloaderSpine || !this.dimensions) return;

    const { width, height } = this.dimensions;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device-specific multiplier
    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.preloaderSpine.scale.set(scale);

    // Center on screen with device-specific Y offset
    const baseOffset = this.symbolHeight || 0;
    this.preloaderSpine.x = width / 2;
    this.preloaderSpine.y = height / 2 - baseOffset * deviceOffsetMultiplier;
  }

  // =========================================================================
  // Animation
  // =========================================================================

  /**
   * Returns array of available animation names from the spine.
   */
  getAvailableAnimations() {
    if (!this.preloaderSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache) return this._availableAnimationsCache;

    this._availableAnimationsCache = this.preloaderSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    return this._availableAnimationsCache;
  }

  /**
   * Starts playing the preloader animation loop.
   */
  playAnimation() {
    if (!this.preloaderSpine?.state || this.isPlaying) return;

    try {
      this.isPlaying = true;
      this.preloaderSpine.state.clearTracks();
      this.preloaderSpine.state.clearListeners();
      this.preloaderSpine.skeleton.setToSetupPose();

      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.preloaderSpine.state.setAnimation(0, animations[0], true);

        // Track first loop completion
        this.preloaderSpine.state.addListener({
          complete: () => {
            if (!this.firstLoopComplete) {
              this.firstLoopComplete = true;
              if (this.loadingComplete) this._resolveOut();
            }
          },
        });
      }
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Stops animation and hides the spine.
   */
  stopAnimation() {
    if (!this.preloaderSpine || !this.initialized) return;

    this.isPlaying = false;
    this.preloaderSpine.alpha = 0.001;
    if (this.preloaderSpine.state) {
      this.preloaderSpine.state.clearTracks();
      this.preloaderSpine.state.clearListeners();
    }
  }

  // =========================================================================
  // Loading lifecycle
  // =========================================================================

  /**
   * Called when assets are fully loaded.
   * Returns a Promise that resolves when at least one full spine loop has played.
   */
  onLoadingComplete() {
    return new Promise((resolve) => {
      if (!this.initialized || !this.preloaderSpine?.state) {
        resolve();
        return;
      }

      this.loadingComplete = true;
      this._outResolve = resolve;

      if (this.firstLoopComplete) {
        this._resolveOut();
      }
      // else: spine listener will call _resolveOut when current loop finishes
    });
  }

  /**
   * Internal: resolve the loading promise and stop the spine.
   */
  _resolveOut() {
    this.stopAnimation();
    if (this._outResolve) {
      this._outResolve();
      this._outResolve = null;
    }
  }

  // =========================================================================
  // Error UI — identical layout/multipliers to WarningPopup / InfoPopup
  // =========================================================================

  _getErrorDeviceConfig() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    const width = this.dimensions?.width || 0;
    const configs = ERROR_POPUP_CONFIG.DEVICE_CONFIGS;

    if (device === PixiSettings.DEVICE.DESKTOP) {
      let desktopSize;
      if (width <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH) {
        desktopSize = "SMALL_DESKTOP";
      } else if (width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH) {
        desktopSize = "WIDE_DESKTOP";
      } else {
        desktopSize = "DESKTOP";
      }
      return (configs[desktopSize] || configs[PixiSettings.DEVICE.DESKTOP])[
        PixiSettings.ORIENTATION.LANDSCAPE
      ];
    }

    if (isMobileSmall) {
      return (
        configs.MOBILE_SMALL?.[orientation] ||
        configs[PixiSettings.DEVICE.MOBILE][orientation]
      );
    }

    return (
      configs[device]?.[orientation] ||
      configs[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE]
    );
  }

  _destroyErrorUI() {
    if (this.errorRoot) {
      try {
        anime.remove(this.errorRoot);
        if (this.errorContainer) anime.remove(this.errorContainer.scale);
        if (this.container?.children?.includes(this.errorRoot)) {
          this.container.removeChild(this.errorRoot);
        }
        this.errorRoot.destroy({ children: true });
      } catch {
        // Silent fail
      }
    }
    this.errorRoot = null;
    this.errorContainer = null;
    this._errorOverlay = null;
    this._errorButtonSprite = null;
    this._isErrorPressAnimating = false;
    this._errorBrightnessFilter = null;
  }

  /**
   * Show error fallback UI — WarningPopup-identical layout, fonts, multipliers,
   * overlay fade + scale-in. Reload button (init failed before game assets).
   */
  showError(message = "Something went wrong.\nPlease reload the game.") {
    this.errorOccurred = true;
    this._errorMessage = message;

    // Remove spine if it exists
    if (this.preloaderSpine) {
      try {
        if (this.preloaderSpine.state) {
          this.preloaderSpine.state.clearListeners();
          this.preloaderSpine.state.clearTracks();
        }
        if (this.container?.children?.includes(this.preloaderSpine)) {
          this.container.removeChild(this.preloaderSpine);
        }
        this.preloaderSpine.destroy();
      } catch {
        // Silent fail
      }
      this.preloaderSpine = null;
    }

    this._buildErrorUI(message, { animate: true });
  }

  _buildErrorUI(message, { animate = false } = {}) {
    this._destroyErrorUI();

    const { width, height } = this.dimensions || { width: 800, height: 600 };
    const centerX = width / 2;
    const centerY = height / 2;

    const deviceConfig = this._getErrorDeviceConfig();
    const deviceScale = deviceConfig?.scaleMultiplier || 1.0;
    const scaledW = (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * deviceScale;
    const scaledH = (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * deviceScale;
    const maxWidth = width * 0.85;
    const maxHeight = height * 0.85;
    const widthRatio = scaledW > maxWidth ? maxWidth / scaledW : 1;
    const heightRatio = scaledH > maxHeight ? maxHeight / scaledH : 1;
    const popupScale = deviceScale * Math.min(widthRatio, heightRatio);

    const panelWidth = (PANEL_REF_WIDTH / DESKTOP_SCALE_REF) * popupScale;
    const panelHeight = (PANEL_REF_HEIGHT / DESKTOP_SCALE_REF) * popupScale;
    const paddingH = ERROR_POPUP_CONFIG.basePaddingH * popupScale;
    const paddingV = ERROR_POPUP_CONFIG.basePaddingV * popupScale;
    const buttonFontSize = Math.round(
      ERROR_POPUP_CONFIG.baseButtonFontSize * popupScale,
    );
    const borderRadius = ERROR_POPUP_CONFIG.baseBorderRadius * popupScale;
    const contentWidth = ERROR_POPUP_CONFIG.baseContentWidth * popupScale;
    const buttonWidth = BUTTON_REF_WIDTH * popupScale;
    const buttonHeight = BUTTON_REF_HEIGHT * popupScale;

    // Auto-shrink message font to fit fixed panel (identical to WarningPopup)
    const textAreaTop = paddingV * TEXT_AREA_TOP_MULTIPLIER;
    const textAreaBottom =
      paddingV * BOTTOM_PADDING_MULTIPLIER +
      buttonHeight +
      paddingV * TEXT_TO_BUTTON_GAP_MULTIPLIER;
    const availableTextHeight = panelHeight - textAreaTop - textAreaBottom;

    let messageFontSize = Math.round(
      ERROR_POPUP_CONFIG.baseMessageFontSize * popupScale,
    );
    const measure = (size) => {
      const style = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        breakWords: true,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      });
      return TextMetrics.measureText(message, style).height;
    };
    const measuredHeight = measure(messageFontSize);
    if (measuredHeight > availableTextHeight && measuredHeight > 0) {
      messageFontSize = Math.max(
        10,
        Math.floor(messageFontSize * (availableTextHeight / measuredHeight)),
      );
    }

    const panelX = centerX - panelWidth / 2;
    const panelY = centerY - panelHeight / 2;

    const textAreaTopAbs = panelY + paddingV * TEXT_AREA_TOP_MULTIPLIER;
    const textAreaBottomAbs =
      panelY +
      panelHeight -
      paddingV * BOTTOM_PADDING_MULTIPLIER -
      buttonHeight -
      paddingV * TEXT_TO_BUTTON_GAP_MULTIPLIER;
    const messageY = (textAreaTopAbs + textAreaBottomAbs) / 2;
    const buttonY =
      panelY +
      panelHeight -
      paddingV * (BOTTOM_PADDING_MULTIPLIER + BUTTON_EXTRA_LIFT_MULTIPLIER) -
      buttonHeight / 2;

    const exclamationX = panelX + paddingH * 0.5;
    const exclamationY = panelY + paddingV * 0.15;
    const exclamationFontSize = Math.round(messageFontSize * 1.8);

    // Root holds overlay + content
    this.errorRoot = new PIXI.Container();
    this.errorRoot.name = "loader_error_popup";
    this.errorRoot.zIndex = 10000;
    this.errorRoot.sortableChildren = true;
    this.container.addChild(this.errorRoot);

    // Overlay (blocks clicks)
    this._errorOverlay = new PIXI.Graphics();
    this._errorOverlay.beginFill(OVERLAY_BACKGROUND_COLOR);
    this._errorOverlay.drawRect(0, 0, width, height);
    this._errorOverlay.endFill();
    this._errorOverlay.alpha = animate ? 0 : OVERLAY_BACKGROUND_ALPHA;
    this._errorOverlay.eventMode = "static";
    this._errorOverlay.zIndex = 10000;
    this._errorOverlay.on("pointerdown", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
    });
    this.errorRoot.addChild(this._errorOverlay);

    // Content container — scale animation pivots around screen center
    this.errorContainer = new PIXI.Container();
    this.errorContainer.name = "popup_content";
    this.errorContainer.zIndex = 10001;
    this.errorContainer.x = centerX;
    this.errorContainer.y = centerY;
    this.errorContainer.pivot.set(centerX, centerY);
    if (animate) {
      this.errorContainer.scale.set(0);
    }
    this.errorRoot.addChild(this.errorContainer);

    // Panel
    const panel = new PIXI.Graphics();
    panel.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
    panel.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, borderRadius);
    panel.endFill();
    this.errorContainer.addChild(panel);

    // Exclamation
    const exclText = new PIXI.Text("!", {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: exclamationFontSize,
      fontWeight: "700",
      fill: 0xffffff,
      align: "center",
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
    exclText.anchor.set(0, 0);
    exclText.x = exclamationX;
    exclText.y = exclamationY;
    exclText.resolution = 2;
    this.errorContainer.addChild(exclText);

    // Message
    const messageText = new PIXI.Text(message, {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: messageFontSize,
      fontWeight: "700",
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: contentWidth,
      breakWords: true,
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
    messageText.anchor.set(0.5);
    messageText.x = centerX;
    messageText.y = messageY;
    messageText.resolution = 2;
    this.errorContainer.addChild(messageText);

    // Button container (identical structure to WarningPopup OK button)
    const buttonContainer = new PIXI.Container();
    buttonContainer.name = "ok_button";
    buttonContainer.x = centerX;
    buttonContainer.y = buttonY;
    this.errorContainer.addChild(buttonContainer);

    const hitArea = new PIXI.Graphics();
    hitArea.beginFill(0x000000, 0.01);
    hitArea.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
    hitArea.endFill();
    hitArea.eventMode = "static";
    hitArea.cursor = "pointer";
    hitArea.hitArea = new PIXI.Rectangle(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
    );
    buttonContainer.addChild(hitArea);

    // Button fill = GameRulesOverlay scrollbar track/background (#383838)
    const SCROLLBAR_TRACK_COLOR = 0x383838;
    const btnRadius = Math.min(12, buttonHeight * 0.2);
    const buttonBg = new PIXI.Graphics();
    buttonBg.beginFill(SCROLLBAR_TRACK_COLOR);
    buttonBg.drawRoundedRect(
      -buttonWidth / 2,
      -buttonHeight / 2,
      buttonWidth,
      buttonHeight,
      btnRadius,
    );
    buttonBg.endFill();
    buttonBg.eventMode = "none";
    this._errorButtonSprite = buttonBg;
    buttonContainer.addChild(buttonBg);

    const buttonText = new PIXI.Text("Reload", {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: buttonFontSize,
      fontWeight: "700",
      fill: 0xffffff,
      align: "center",
      ...TEXT_STYLE_CONFIG.OUTLINE,
    });
    buttonText.anchor.set(0.5);
    buttonText.resolution = 2;
    buttonText.eventMode = "none";
    buttonContainer.addChild(buttonText);

    this._errorBrightnessFilter = new PIXI.ColorMatrixFilter();
    this._errorBrightnessFilter.brightness(2.0, false);

    hitArea.on("pointerover", () => {
      if (this._errorButtonSprite && this._errorButtonSprite.filters?.length !== 1) {
        this._errorButtonSprite.filters = [this._errorBrightnessFilter];
      }
    });
    hitArea.on("pointerout", () => {
      if (this._errorButtonSprite) {
        this._errorButtonSprite.filters = [];
      }
    });
    hitArea.on("pointertap", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (this._isErrorPressAnimating) {
        window.location.reload();
        return;
      }
      this._isErrorPressAnimating = true;
      if (this._errorButtonSprite && this._errorButtonSprite.width !== undefined) {
        createButtonPressAnimation(this._errorButtonSprite, {
          targetWidth: buttonWidth,
          targetHeight: buttonHeight,
          onComplete: () => {
            this._isErrorPressAnimating = false;
            window.location.reload();
          },
        });
      } else {
        window.location.reload();
      }
    });

    if (animate) {
      anime({
        targets: this._errorOverlay,
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
      });
      anime({
        targets: this.errorContainer.scale,
        x: 1,
        y: 1,
        duration: POPUP_SCALE_DURATION,
        delay: POPUP_SCALE_DELAY,
        easing: POPUP_SCALE_EASING,
      });
    }
  }

  // =========================================================================
  // Dimension updates
  // =========================================================================

  /**
   * Updates dimensions and re-positions the spine.
   */
  updateDimensions(dimensions, symbolHeight, dimensionsContext) {
    const deviceOrOrientationChanged =
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.dimensionsContext?.isMobileSmall !== dimensionsContext?.isMobileSmall;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    // Jitter guard: skip small dimension changes (e.g. mobile address bar)
    // Only reposition on significant size changes (>50px) or device/orientation change
    const widthDelta = Math.abs((this.dimensions?.width || 0) - (dimensions?.width || 0));
    const heightDelta = Math.abs((this.dimensions?.height || 0) - (dimensions?.height || 0));
    const significantSizeChange = widthDelta > 50 || heightDelta > 50;

    // Background always tracks viewport — needed for fullscreen hit area
    this.dimensions = dimensions;
    this._updateBackgroundDimensions();

    if (!deviceOrOrientationChanged && !symbolHeightChanged && !significantSizeChange) {
      return;
    }

    this.symbolHeight = symbolHeight;
    if (dimensionsContext) {
      if (deviceOrOrientationChanged) {
        this._deviceMultipliersCache = null;
      }
      this.dimensionsContext = dimensionsContext;
    }

    if (this.initialized && !this.errorOccurred) {
      this.applyScaleAndCenter();
    }

    // Rebuild WarningPopup-identical error UI on resize (no re-animate)
    if (this.errorOccurred && this._errorMessage) {
      this._buildErrorUI(this._errorMessage, { animate: false });
    }
  }

  // =========================================================================
  // Cleanup
  // =========================================================================

  cleanup() {
    this.isPlaying = false;
    this._backgroundClickHandler = null;
    this._destroyErrorUI();

    if (this._backgroundGraphics) {
      this._backgroundGraphics.removeAllListeners();
      if (this.container?.children?.includes(this._backgroundGraphics)) {
        this.container.removeChild(this._backgroundGraphics);
      }
      this._backgroundGraphics.destroy();
      this._backgroundGraphics = null;
    }

    // Clean up spine
    if (this.preloaderSpine) {
      try {
        if (this.preloaderSpine.state) {
          this.preloaderSpine.state.clearListeners();
          this.preloaderSpine.state.clearTracks();
          this.preloaderSpine.state.timeScale = 1.0;
        }
        if (this.container?.children.includes(this.preloaderSpine)) {
          this.container.removeChild(this.preloaderSpine);
        }
        this.preloaderSpine.destroy();
      } catch (error) {
        // ignore
      }
      this.preloaderSpine = null;
    }

    this.initialized = false;
    this._availableAnimationsCache = null;
  }
}

export default LoaderController;
